import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SocialLogin from '../components/SocialLogin';
import AuthLayout from '../components/auth/AuthLayout';
import AuthError from '../components/auth/AuthError';
import DevAuthButton from '../components/auth/DevAuthButton';
import LinkAccountNotice from '../components/auth/LinkAccountNotice';
import PasswordAuthForm from '../components/auth/PasswordAuthForm';
import GuestIdSignInForm from '../components/auth/GuestIdSignInForm';
import SignInMethodsLoading from '../components/auth/SignInMethodsLoading';
import { AUTH_LINK_CLASSES, AUTH_SECONDARY_LINK_CLASSES } from '../components/auth/authLinkClasses';
import { useAuthStore } from '../store/authStore';
import { useSignInMethods } from '../hooks/useSignInMethods';
import { useTurnstile } from '../hooks/useTurnstile';
import { TURNSTILE_ACTIONS } from '@kaeliux/shared';
import { Panel } from '../components/ui/Panel';
import { Spinner } from '../components/ui/Spinner';
import { cn } from '../utils/cn';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, passwordSignIn, guestSignIn, signInWithGuestId, clearError, pendingLink } = useAuthStore();
    const [pending, setPending] = useState<'dev' | 'guest' | 'guestId' | null>(null);
    const methods = useSignInMethods();
    const guestSignUpCheck = useTurnstile(TURNSTILE_ACTIONS.guestSignUp);
    const guestSignInCheck = useTurnstile(TURNSTILE_ACTIONS.guestSignIn);

    useEffect(() => {
        clearError();
    }, [clearError]);

    const goHome = () => navigate('/');

    const run = async (kind: 'dev' | 'guest' | 'guestId', signIn: () => Promise<void>) => {
        setPending(kind);
        try {
            await signIn();
            goHome();
        } catch (error) {
            console.error('Sign-in failed:', error);
        } finally {
            setPending(null);
        }
    };

    // Shown whatever the guest switch says: turning off new guest sign-ups (or a failed
    // methods read) must not lock existing guests out of their accounts.
    const guestIdForm = (
        <GuestIdSignInForm
            disabled={pending !== null}
            onSubmit={(guestId) => run('guestId', () => signInWithGuestId(guestId, guestSignInCheck.getToken))}
        />
    );

    return (
        <AuthLayout tagline="Command your fleet, conquer the galaxy">
            <Panel variant="glass" padding="lg" className="flex flex-col gap-2">
                <div className="mb-3 flex flex-col gap-1">
                    <h2 className="text-heading text-content-primary">Sign in</h2>
                    <p className="text-content-muted text-body">
                        New here?{' '}
                        <Link to="/register" className={AUTH_LINK_CLASSES}>
                            Create account
                        </Link>
                    </p>
                </div>

                <LinkAccountNotice />
                <AuthError />

                {!methods ? (
                    <SignInMethodsLoading />
                ) : !methods.emailPassword && !methods.guest && methods.social.length === 0 ? (
                    <>
                        <p className="text-content-secondary text-body">
                            Signing in isn&apos;t available right now. Please try again later.
                        </p>
                        <div className="mt-6 flex w-full flex-col items-center">{guestIdForm}</div>
                    </>
                ) : (
                    <>
                        {methods.emailPassword && (
                            <PasswordAuthForm
                                mode="signIn"
                                submitLabel="Sign in"
                                onSubmit={async (email, password) => {
                                    await passwordSignIn(email, password);
                                    goHome();
                                }}
                            />
                        )}

                        {(methods.emailPassword || (methods.emailLink && !pendingLink)) && (
                            <div className="flex flex-wrap justify-between gap-x-4">
                                {methods.emailPassword && (
                                    <Link to="/forgot-password" className={AUTH_SECONDARY_LINK_CLASSES}>
                                        Forgot password?
                                    </Link>
                                )}
                                {/* The waiting provider credential only lives in this page, so a sign-in that
                                    comes back through an emailed link could not complete the connection. */}
                                {methods.emailLink && !pendingLink && (
                                    <Link to="/login/email-link" className={AUTH_SECONDARY_LINK_CLASSES}>
                                        Email me a sign-in link
                                    </Link>
                                )}
                            </div>
                        )}

                        <SocialLogin onLoginSuccess={goHome} variant="login" divider={methods.emailPassword} />

                        {methods.guest && (
                            <div className="mt-6 flex flex-col items-center gap-1 text-center">
                                <p className="text-content-muted text-body">
                                    Just looking around?{' '}
                                    <button
                                        type="button"
                                        className={cn(AUTH_LINK_CLASSES, 'inline-flex items-center gap-1.5 disabled:cursor-wait disabled:opacity-70')}
                                        aria-busy={pending === 'guest' || undefined}
                                        disabled={pending !== null}
                                        onClick={() => run('guest', () => guestSignIn(guestSignUpCheck.getToken))}
                                    >
                                        {pending === 'guest' ? (
                                            <>
                                                <Spinner className="h-3.5 w-3.5" /> Starting guest game
                                            </>
                                        ) : (
                                            'Play as guest'
                                        )}
                                    </button>
                                </p>
                                <p className="text-content-muted text-micro">
                                    Save the guest ID shown in game to come back later, or create an account to keep
                                    your progress for good.
                                </p>
                            </div>
                        )}

                        <div className={cn('flex w-full flex-col items-center', methods.guest ? 'mt-2' : 'mt-6')}>
                            {guestIdForm}
                        </div>
                    </>
                )}

                {/* Bot-check widgets stay mounted in one place whatever the layout, so they never
                    end up in a detached element when the sign-in methods refresh. */}
                <div ref={guestSignUpCheck.containerRef} className="flex justify-center" />
                <div ref={guestSignInCheck.containerRef} className="flex justify-center" />

                <DevAuthButton
                    label="Quick Login (Mock)"
                    loadingLabel="Logging in..."
                    isLoading={pending === 'dev'}
                    onClick={() => run('dev', login)}
                />

                <p className="mt-6 text-content-muted text-label">
                    By signing in, you agree to our{' '}
                    <Link to="/terms" className="underline underline-offset-2 hover:text-content-primary">
                        Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="underline underline-offset-2 hover:text-content-primary">
                        Privacy Policy
                    </Link>
                </p>
            </Panel>
        </AuthLayout>
    );
}
